import PropTypes from 'prop-types';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import {
  editPageViewsApi,
  loadPageViewsApi,
  pushPageViewsApi,
  updatePageViewsApi,
} from '../services/PageViewsApi';
import { getYM } from './Common/analytics';

// Create a React Context for page views
const PageViewsContext = createContext();

// Create a React Hook to access page views context
export function usePageViews() {
  const context = React.useContext(PageViewsContext);
  if (!context) {
    throw new Error(
      `usePageViews must be used within a PageViewsProvider`,
    );
  }
  return context;
}

export function PageViewsProvider(props) {
  const [memberOnePv, setMemberOnePv] = useState();

  const loadPageViews = useCallback(async (
    memberId,
    ymRef = getYM(),
    setPvState = true,
  ) => {
    try {
      const pvArray = await loadPageViewsApi(memberId, ymRef);

      if (setPvState) {
        setMemberOnePv(pvArray);
      }
      return pvArray;
    } catch (error) {
      console.error('Error loading page views:', error);
      const pvArray = [];
      if (setPvState) {
        setMemberOnePv(pvArray);
      }
      return pvArray;
    }
  }, []);

  const pushPageViews = useCallback(async (
    memberId,
    url,
    pvStart,
    pvEnd,
    desc,
    ymRef = getYM(),
    setPvState = true,
  ) => {
    try {
      const pageViewData = {
        url,
        pvStart,
        pvEnd,
        desc,
        ymRef,
      };

      const pvItem = await pushPageViewsApi(memberId, pageViewData);

      if (setPvState) {
        setMemberOnePv((prev) => [...(prev || []), pvItem]);
      }

      return 0;
    } catch (error) {
      console.error('Error creating manual page view:', error);
    }
  }, []);

  const editPageViews = useCallback(async (
    memberId,
    start,
    exclude,
    ymRef = getYM(),
    setPvState = true,
  ) => {
    try {
      const pvArray = await editPageViewsApi(
        memberId,
        start,
        exclude,
        ymRef,
      );
      if (setPvState) {
        setMemberOnePv(pvArray);
      }

      return pvArray;
    } catch (error) {
      console.error('Error editing page views:', error);
    }
  }, []);

  const updatePageViews = useCallback(async (memberId, url, pvStart, pvEnd) => {
    try {
      const path = url.split('/').slice(0, 5).join('/');
      const yearMonth = getYM(); // NOTE: always on the current year month

      await updatePageViewsApi(memberId, path, pvStart, pvEnd, yearMonth);
    } catch (error) {
      console.error('Error updating page views:', error);
    }
  }, []);

  const value = useMemo(() => {
    return {
    memberOnePv,
    pageViewActions: {
      loadPageViews,
      pushPageViews,
      editPageViews,
      updatePageViews,
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOnePv]);

  return (
    <PageViewsContext.Provider value={value}>
      {props.children}
    </PageViewsContext.Provider>
  );
}

PageViewsProvider.propTypes = {
  children: PropTypes.element,
};
